/* eslint-disable rxjs/no-subject-value */
import { EventEmitter, Injectable, Injector } from '@angular/core';

import { TranslateParser } from '@ngx-translate/core';
import { Store } from '@ngrx/store';
import { Observable, from, of, Subject, BehaviorSubject } from 'rxjs';
import { catchError } from 'rxjs/operators';

import KeywordTemplates from '../../assets/template/keyword.template.json';
import { AppStore } from '../store/states';
import {
  KeywordTraverseAction,
  SelectedForDeleteAction,
} from '../store/actions/app.custom-actions';
import { WKMessageDialogComponent } from '../components/shared/dialogs/message-dialog/message.dialog';
import { WKMessageDialogService } from '../components/shared/dialogs/message-dialog/message.dialog.service';
import {
  IKeyWord,
  IKeywordDefinition,
  IdefinitionId,
} from '../store/states/keyword.state';
import { LocalStorage } from '../shared';
import { SiteConfig } from '../types';
import { SiteConfigService } from './site-config.service';
import { CitationService } from './citations.service';
import { CKEditorComponent } from '../components/ckeditor';
import { getFormattedKeyword } from '../store/selectors/app.custom-selectors';
import { WkAlfrescoApiService } from './wk-alfresco-api.service';

const { CURRENT_SITE } = LocalStorage;

@Injectable({
  providedIn: 'root',
})
export class WkKeywordService {
  definitionId: IdefinitionId;
  editKeyword: EventEmitter<IKeywordDefinition> =
    new EventEmitter<IKeywordDefinition>();
  keywordEditor = new Subject<CKEditorComponent>();
  addedKeyDefData = new Subject<CKEditorComponent>();
  private readonly messageService: WKMessageDialogService;
  private readonly citationService: CitationService;
  closeDefinitionEditor = new EventEmitter<boolean>();
  chevronNav = new Subject<any>();
  expandPanel = new EventEmitter<string>();
  public keywords = [];
  toggledChevron = new BehaviorSubject<boolean>(false);
  isNavigation = new BehaviorSubject<boolean>(false);
  constructor(
    private readonly translate: TranslateParser,
    private readonly apiService: WkAlfrescoApiService,
    private readonly siteConfig: SiteConfigService,
    private readonly injector: Injector,
    private readonly store: Store<AppStore>
  ) {
    this.messageService = this.injector.get<WKMessageDialogService>(
      WKMessageDialogService
    );
    this.citationService = this.injector.get<CitationService>(CitationService);
    this.store
      .select(getFormattedKeyword)
      .subscribe((keywords: IKeywordDefinition[]) => {
        this.keywords = keywords;
      });
  }

  public get getDefId() {
    return this.definitionId;
  }

  public set setDefId(ids: IdefinitionId) {
    this.definitionId = ids;
  }

  public getKeyTermTemplate(props: { def: string; keyword: string }): string {
    const { labelId, defId } = this.getDefId;
    const { def, keyword } = props;
    const {
      keyword: { baseUrl },
    } = this.getCurrentSite();
    return this.translate.interpolate(
      KeywordTemplates.KEY_WORD_CREATE_TEMPLATE,
      {
        def: this.encodeForTemplate(def),
        keyword: this.encodeForTemplate(keyword),
        defId,
        labelId,
        baseUrlWithDefId: `${baseUrl}/resource/keyterm/${defId}`,
        baseUrlWithLabelId: `${baseUrl}/resource/label/${labelId}`,
      }
    );
  }

  public getKeyWordData(nodeId): Observable<{ keyWords: IKeyWord[] }> {
    const contentType = ['application/json'];
    return from(
      this.apiService.callContentClientCustomApi({
        path: `alfresco/s/wk/nodes/${nodeId}/keywords`,
        httpMethod: 'GET',
        contentTypes: contentType,
        accepts: contentType,
      })
    ).pipe(catchError(() => of({ keyWords: [] })));
  }

  public setkeyDefinitionEditType(data: CKEditorComponent) {
    this.addedKeyDefData.next(data);
  }

  public getkeyDefinitionEditType(): Observable<CKEditorComponent> {
    return this.addedKeyDefData.asObservable();
  }

  public setKeywordEditorInstance(data: CKEditorComponent) {
    this.keywordEditor.next(data);
  }

  public getKeywordEditorInstance(): Observable<CKEditorComponent> {
    return this.keywordEditor.asObservable();
  }

  /**
   * Fetch current site details from local storage
   *
   * @returns { SiteConfig }
   */
  public getCurrentSite(): SiteConfig {
    const currentSite = localStorage.getItem(CURRENT_SITE);
    return this.siteConfig.getCurrentSiteObject(currentSite);
  }

  /**
   * Create & return alternate label template
   *
   * @public
   * @param {string} keyword
   * @returns {string}
   */
  public getAlternateLabelTemplate(keyword: string): string {
    const { labelId, defId } = this.getDefId;
    const {
      keyword: { baseUrl },
    } = this.getCurrentSite();
    return this.translate.interpolate(KeywordTemplates.ALT_LABEL_TEMPLATE, {
      keyword: this.encodeForTemplate(keyword),
      defId,
      labelId,
      baseUrlWithDefId: `${baseUrl}/resource/keyterm/${defId}`,
      baseUrlWithLabelId: `${baseUrl}/resource/label/${labelId}`,
    });
  }

  private encodeForTemplate(value: string): string {
    return value.replace(/\"/g, '&quot').replace(/\'/g, '&apos');
  }

  /**
   * This will call the Confirm dialog for delete action on keyword
   *
   * @public
   * @param {string} keyword
   * @returns {string}
   */
  public deleteKeyword(keyword: IKeywordDefinition, unwrapFlag: boolean) {
    const dialog = this.confirmPopup();
    dialog.afterClosed().subscribe((result) => {
      if (result) {
        const newKeyword = {
          keyword,
          unwrapFlag,
        };
        this.store.dispatch(new SelectedForDeleteAction(newKeyword));
        this.citationService.switchTab();
      }
    });
  }

  confirmPopup() {
    const data = {
      message: 'APP.KEYWORDS_DEFINITION.DELETE_KEYWORD_MESSAGE_DIALOG.MESSAGE',
      headerLabel:
        'APP.KEYWORDS_DEFINITION.DELETE_KEYWORD_MESSAGE_DIALOG.HEADER',
      okLabel: 'APP.COMMON.DELETE',
      cancelLabel: 'APP.COMMON.CANCEL',
      okLabelClass: 'wk-button-danger',
      cancelLabelClass: 'wk-button-text',
    };
    return this.messageService.openMessage(WKMessageDialogComponent, data);
  }

  /**
   * Traverse keyword
   *
   * @public
   */
  public traverseKeword() {
    const idToSelect = localStorage.getItem(
      LocalStorage.KEYWORD_ID_SELECT_FOR_CHEVRON
    );
    this.store
      .select(getFormattedKeyword)
      .subscribe((keywords: IKeywordDefinition[]) => {
        keywords.forEach((keyword: IKeywordDefinition) => {
          const keywordFound = keyword.altLabels.find(
            (altLabel) => altLabel.id === idToSelect
          );
          if (keywordFound && this.toggledChevron.value) {
            this.store.dispatch(
              new KeywordTraverseAction({ keyword, idToSelect })
            );
          }
        });
      });
  }
}
